import { MARKETPLACES, EXTERNAL_PARTNERS } from '../lib/constants';
import { get, intersection, find } from 'lodash';
import { clientLogger } from './logger';
const DYNAMIC_CATEGORIES = ['discover', 'favorites'];

function trackImpression({
  custId,
  pgrp,
  page,
  blid,
  sltp,
  xe,
  xt,
  unique_message_id
}) {
  const fptiData = {
    custId,
    pgrp,
    page,
    blid,
    sltp,
    xe,
    xt,
    unique_message_id
  };
  window.PAYPAL.analytics.Analytics.prototype.recordImpression(null, fptiData);
}

function trackClick({
  custId,
  pgrp,
  page,
  blid,
  sltp,
  xe,
  xt,
  unique_message_id,
  link
}) {
  const fptiData = {
    custId,
    pgrp,
    page,
    blid,
    sltp,
    xe,
    xt,
    unique_message_id,
    link
  };
  window.PAYPAL.analytics.Analytics.prototype.recordClick({ data: fptiData });
}

//replaces all numbers and username and service provider name in an email address with *'s
const sanitizedForSensitiveInformation = response => {
  const lowerIntegerVal = 47;
  const upperIntegerVal = 58;
  if (typeof response === 'string') {
    for (let i = 0; i < response.length; i++) {
      //Prunes and hides numbers in survey response
      if (
        response.charCodeAt(i) > lowerIntegerVal &&
        response.charCodeAt(i) < upperIntegerVal
      ) {
        response = response.substr(0, i) + '*' + response.substr(i + 1);
      }

      //Hides username before @ sign
      if (response[i] === '@') {
        let j = i - 1;
        while (response[j] !== ' ' && j >= 0) {
          response = response.substr(0, j) + '*' + response.substr(j + 1);
          j--;
        }
        //Hides service provider after @ sign
        let k = i + 1;
        while (response[k] !== '.') {
          response = response.substr(0, k) + '*' + response.substr(k + 1);
          k++;
        }
      }
    }
  }
  return response;
};

const checkForEmptyCategory = (category, tools) => {
  const name = get(category, 'name', ''),
    items = get(category, 'items', []),
    toolNames = Object.keys(tools);

  // remove only if it is static categories and have 0 tools
  return (
    !DYNAMIC_CATEGORIES.includes(name) &&
    !intersection(items, toolNames).length &&
    !intersection(items, MARKETPLACES).length &&
    !intersection(items, EXTERNAL_PARTNERS).length
  );
};

const fireDetailsPageComponentFailureFpti = ({
  data = {},
  componentName = '',
  fptiImpression = () => ({}),
  code = 'code'
}) => {
  if (typeof window === 'undefined') return;
  const propsList = Object.keys(data)
    .map(k => `${k}: ${data[k]}`)
    .join(', ');
  const errPageString = `${componentName} failure. One of ${propsList} missing in ${componentName}.js`;
  const feedback = window.feedback || {};
  fptiImpression({
    ...feedback,
    erpg: errPageString,
    eccd: code
  });
};

const buildDefaultAltTag = (sectionName, url) =>
  `${sectionName || ''} ${
    (url || '')
      .split('/')
      .pop()
      .split('.')[0]
  }`;

const preventTabNabbing = url => {
  const constructorUrl = new URL(url, 'https://www.paypal.com');
  if (constructorUrl.hostname.includes('paypal.com')) {
    return 'noopener';
  }
  return 'noopener noreferrer';
};

const parseData = jsonString => {
  let data = [];
  try {
    data = JSON.parse(jsonString);
  } catch (err) {
    clientLogger.warn(
      `Corrupt JSON while parsing photo row ${jsonString}, Error: ${err}`
    );
  }
  return data;
};

const getUtmParams = () => {
  const result = {};
  if (typeof document !== 'undefined') {
    try {
      const url = document.location.href;
      const constructorUrl = new URL(url);
      const utmParams = [
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_content'
      ];
      utmParams.forEach(utmParam => {
        const utmValue = constructorUrl.searchParams.get(utmParam);
        if (utmValue) {
          result[utmParam] = utmValue;
        }
      });
    } catch (err) {
      clientLogger.warn(`UTM Parsing Error : ${err}`);
    }
  }
  return result;
};

const getDefaultGetStartedApps = (getStartedMsg, tools) => {
  const filterGetStartedMsg = getStartedMsg.filter(msg => {
    return find(tools, tool => tool.appKey === msg.appKey);
  });
  return filterGetStartedMsg.slice(0, 4);
};

const getStaffPickAppKeys = staffPickDisplayed => {
  return staffPickDisplayed.map(({ appKey }) => appKey);
};

const getStaffPickCategories = (staffPickAppKeys, tools) => {
  const toolsForCategories = tools.filter(tool => {
    return find(staffPickAppKeys, key => key === tool.appKey);
  });

  return toolsForCategories.map(({ categoryKey }) => categoryKey);
};

const getRecommendationKeys = recommendations => {
  const recommendedCategories = [];
  const recommendedApps = [];

  recommendations.forEach(({ categoryKey, appKey }) => {
    recommendedCategories.push(categoryKey);
    recommendedApps.push(appKey);
  });

  return { recommendedCategories, recommendedApps };
};

const getIntentCategories = (intentRowKeys, tools) => {
  const toolsForCategories = tools.filter(tool => {
    return find(intentRowKeys, key => key === tool.appKey);
  });

  return toolsForCategories.map(({ categoryKey }) => categoryKey);
};

const getStaffPicksMsg = (
  staffPickMsg,
  defaultRecommended,
  isLoggedIn,
  tools
) => {
  const staffPickMsgFiltered = staffPickMsg.filter(msg => {
    return find(tools, tool => tool.appKey === msg.appKey);
  });

  if (isLoggedIn) {
    return staffPickMsgFiltered
      .filter(t => {
        return !defaultRecommended.some(d => {
          return d.appKey === t.appKey;
        });
      })
      .slice(0, 4);
  }
  return staffPickMsgFiltered.slice(0, 4);
};

export {
  trackImpression,
  trackClick,
  sanitizedForSensitiveInformation,
  checkForEmptyCategory,
  fireDetailsPageComponentFailureFpti,
  buildDefaultAltTag,
  parseData,
  preventTabNabbing,
  getUtmParams,
  getDefaultGetStartedApps,
  getIntentCategories,
  getRecommendationKeys,
  getStaffPickAppKeys,
  getStaffPickCategories,
  getStaffPicksMsg
};
